import { GridApi } from 'ag-grid-community';
import { Injectable, Injector } from '@angular/core';
import { AbstractControl, FormGroup } from '@angular/forms';
import {
  castArray,
  flow,
  get,
  isEmpty,
  isEqual,
  sortBy,
  startCase,
  toLower,
} from 'lodash';

import {
  AnyObject,
  IBaseEntityType,
  CreateFormRules,
  IDocumentErrorMessage,
  IDocumentInfoErrorType,
  IDocumentInfoMessage,
  DocumentMessageTypes,
  EDITOR_MODE,
  ENTITY_TYPES,
  IErrorMessageParams,
  IFormRules,
  IBaseFormService,
  IInfoMessageParams,
  IScheduleData,
  GRID_LIST_VIEW_TYPE,
  IEntityConfig,
  IUserConfig,
  USER_CONFIG_MODULE_NAME,
  IDuplicateOption,
  CreateFormConfig,
  IResolveFormAdditionData,
} from '@shared/types';
import {
  BehaviorSubject,
  Observable,
  observableDefer,
  observableEMPTY,
  observableFrom,
  rx,
  Subscription,
} from '@shared/rx';
import { ValidationService } from '@components/form/services';
import { STATE_SELECTORS } from '../state-service/state-selectors';
import { StateService } from '../state-service/state.service';
import { lazyInject } from '@containers/base/decorators';
import { EntityServiceProvider } from '../datasource/entity-service-provider.service';
import { ActivatedRoute } from '@angular/router';
import { setFormFieldValue } from '@components/form/services/schema-form/schema-form-functions';
import {
  buildFormGetter,
  buildFormReadyObsGetter,
  flatTreeDataByHasChildren,
} from '@shared/utils';
import { ITreeData } from '@shared/types/common/tree.type';
import { ToolsService } from '../tools';
import { XVII18nService } from '@app/i18n/services';
import { I18n } from '@app/i18n/strings-keys';
import {
  ICommonFormConfig,
  MainForm,
  MainOrSubFormGroups,
  MainOrSubFormKey,
} from '@app/components/form/common-form/common-form.types';
import { PermissionService } from '../session/permission.service';
import { MAIN_FORM_KEY } from '@app/containers/base/containers/editor/editor.constants';

@Injectable()
export class BaseFormService implements IBaseFormService<any> {
  public enableRightExpand$: BehaviorSubject<boolean> =
    new BehaviorSubject<boolean>(false);

  public get enableRightExpand(): boolean {
    return this.enableRightExpand$.getValue();
  }

  public set enableRightExpand(value: boolean) {
    this.enableRightExpand$.next(value);
  }

  public get getScheduleValue(): IScheduleData {
    return this.currentScheduling;
  }

  public get gridListView(): GRID_LIST_VIEW_TYPE {
    return GRID_LIST_VIEW_TYPE.CATEGORY_VIEW;
  }

  public set setScheduleValue(schedulingValue: IScheduleData) {
    this.currentScheduling = schedulingValue;
  }
  public static ENTITY_TYPE: ENTITY_TYPES;

  @lazyInject(PermissionService) public permissionService: PermissionService;
  @lazyInject(StateService) public stateService: StateService;
  @lazyInject(ActivatedRoute) public route: ActivatedRoute;
  @lazyInject(EntityServiceProvider)
  public entityServiceProvider: EntityServiceProvider;
  @lazyInject(ToolsService) public toolsService: ToolsService;
  @lazyInject(XVII18nService)
  public i18nService: XVII18nService;

  public documentPromise: Promise<object> = new Promise(
    (res: Function): void => {
      this.documentResolver = res;
    },
  );
  public formPromise: Promise<FormGroup> = new Promise(
    (res: Function): void => {
      this.formResolver = res;
    },
  );
  public documentResolver: Function;
  public document: any;
  protected formResolver: Function;
  protected mode: EDITOR_MODE = EDITOR_MODE.NEW;
  public currentEntityTypeObs: Observable<ENTITY_TYPES> =
    // @ts-ignore
    this.stateService.select(STATE_SELECTORS.CURRENT_ENTITY_TYPE);
  public documentMessages: (IDocumentInfoMessage | IDocumentErrorMessage)[] =
    [];
  // This subscriptions array re-filled after Save button click
  protected subscriptions: Subscription[] = [];
  // This subscriptions array EXIST and doesn't unsubscribe
  protected cleanedOnDestroySubscriptions: Subscription[] = [];
  protected currentEntityType: ENTITY_TYPES;
  // Store all module column config
  protected allModuleColumnConfig: IUserConfig[] = [];
  // Define module name for store column config
  protected moduleNameForColumnConfig: string;
  public form: FormGroup;
  public createFormRulesFunction: CreateFormRules<IBaseFormService<any>>;
  public createRightFormConfigFunction: CreateFormConfig<IBaseFormService<any>>;
  public get formKeys(): Record<'main' | string, string> {
    return {
      main: MAIN_FORM_KEY,
    };
  }
  public entityConfig: IEntityConfig;
  public isDuplicateMode: boolean = false;

  public formReady$: BehaviorSubject<Record<MainForm | string, boolean>>;

  public rightForms: Record<string, FormGroup> = {};
  public rightFormApis: Record<
    string,
    {
      gridApi: GridApi;
    }
  > = {};

  public editor;

  public get canEdit(): boolean {
    return !!this.editor?.canEdit;
  }

  /**
   * use this to get an observable that emits when the specified form(s) are ready
   *
   * will return formGroup when there is only one form key specified
   *
   * @param formKeys the form key or array of form keys to wait for readiness
   * @returns an observable that emits the formGroup(s) when the specified form(s) are ready
   */
  public getFormReadyObs(
    formKeys: MainOrSubFormKey,
  ): Observable<MainOrSubFormGroups> {
    return buildFormReadyObsGetter({
      formReady$: this.formReady$,
      form: this.form,
      rightForms: this.rightForms,
    })(formKeys);
  }

  public getForm(formKey: MainOrSubFormKey): FormGroup | null {
    return buildFormGetter({
      form: this.form,
      rightForms: this.rightForms,
    })(formKey);
  }

  private currentScheduling: IScheduleData;
  public constructor(public injector: Injector) {}
  /**
   * get filter out empty field value change
   *
   * @param fieldPath target field path
   * @returns field value change observable
   */
  protected getFieldChangeFilterEmpty<T = string>(
    fieldPath: string,
  ): Observable<T> {
    return this.fieldValueChanges(fieldPath).pipe(
      rx.filter((value) => !isEmpty(value)),
      rx.distinctUntilChanged(),
    );
  }

  public inject(token: any, notFoundValue?: any): any {
    return this.injector.get(token, notFoundValue);
  }

  public setupEmitWidgetEvent(
    fn: (widgetFieldPath: string, eventName: string, data: AnyObject) => void,
  ): void {
    this.emitWidgetEvent = fn;
  }

  public resolveDocument<T>(doc: T, duplicateOption?: IDuplicateOption): void {
    this.updateFormServiceProperty(duplicateOption);
    this.documentResolver(doc);
    this.document = doc;

    this.onDocumentReady(doc);
    const mode = this.getDocumentOpenMode(this.document);
    this.updateFormMode(mode);
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  public updateFormServiceProperty(duplicateOption?: IDuplicateOption) {}

  /**
   * resolve form with pass form params to this service
   *
   * @param form main form
   * @param additionData right forms & additional fields if u need
   */
  public resolveForm(
    form: FormGroup,
    additionData?: IResolveFormAdditionData,
  ): void {
    const {
      rightForms = {},
      rightFormApis = {},
      formReady$,
    } = additionData ?? {};

    this.form = form;
    this.rightForms = rightForms;
    this.rightFormApis = rightFormApis;
    this.formReady$ = formReady$;

    if (this.document) {
      this.form.patchValue(this.document);
    }

    setTimeout(() => {
      this.formResolver(form);
      this.onFormReady(form);
    }, 0);
  }

  public createFormRules(editor?, ...params): IFormRules {
    this.entityConfig = editor?.entityConfig;
    this.editor = editor;

    return this.createFormRulesFunction(this, editor, ...params);
  }

  public createRightFormConfig(editor?, ...params): ICommonFormConfig {
    return this.createRightFormConfigFunction(this, editor, ...params);
  }

  public updateFormMode(mode: EDITOR_MODE): void {
    this.mode = mode;
  }

  public selectDocument(): Observable<IBaseEntityType> {
    return observableFrom(this.documentPromise);
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  public onFormReady(form: FormGroup): void {}

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  public onDocumentReady(doc): void {}

  public fieldValueChanges = <T = any>(path: string): Observable<T> =>
    observableFrom(
      this.formPromise.then((form: AbstractControl) =>
        observableDefer(() => {
          const control = form.get(path);
          if (!control) return observableEMPTY;

          return control.valueChanges.pipe(rx.startWith(control.value));
        }),
      ),
    ).pipe(rx.switchAll());

  public fieldStatusChanges = (path: string): Observable<any> =>
    observableFrom(
      this.formPromise.then((form: AbstractControl) =>
        observableDefer(() => {
          const control = form.get(path);
          if (!control) return observableEMPTY;

          return control.statusChanges.pipe(rx.startWith(control.value));
        }),
      ),
    ).pipe(rx.switchAll());

  public async linkFieldValidation(...fieldList: string[]): Promise<void> {
    const form: FormGroup = await this.formPromise;

    this.subscriptions.push(
      ValidationService.linkedFieldsValidation(fieldList, form),
    );
  }

  public destroyFormDefinition(): void {
    this.clearSubscriptions();
    this.clearOnDestroySubscriptions();
    this.clearForm();
    this.clearDocument();
    this.clearDocumentMessages();
    this.enableRightExpand$.next(false);
    this.setScheduleValue = null;
  }

  public initUpdateValueAndValidityFieldControl(fieldKey: string): void {
    const fieldControl: AbstractControl = this.getFormFieldControl(fieldKey);
    if (!isEmpty(fieldControl)) fieldControl.updateValueAndValidity();
  }

  public getFormFieldControl(path: string): AbstractControl | null {
    if (!this.form) {
      throw Error(
        'getFormFieldControl: property "form" does not exist in editor',
      );
    }

    return this.form.get(path);
  }

  public getFormFieldValue<T>(path: string): T {
    return this.getFormFieldControl(path).value;
  }

  public getFormFieldDirty(path: string): boolean {
    return this.getFormFieldControl(path)?.dirty ?? false;
  }

  public markAndSetValue(controlPath: string, value: any): void {
    const control = this.getFormFieldControl(controlPath);

    control.setValue(value);
    control.markAsTouched();
    control.markAsDirty();
  }

  public async getInfoMessageInformation(): Promise<
    (IDocumentInfoMessage | IDocumentErrorMessage)[]
  > {
    await this.documentPromise;

    const message: IDocumentErrorMessage | IDocumentInfoMessage = await (<
      IDocumentInfoMessage | IDocumentErrorMessage
    >flow(this.deletedDocumentHandler.bind(this))());
    if (message) this.documentMessages.push(message);

    return this.documentMessages;
  }

  public isDocumentWithMessage(): boolean {
    return !get(this.document, 'is_active', true);
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  public saveUserConfig(moduleName: USER_CONFIG_MODULE_NAME): void {}

  protected applyColumnState(): void {}

  protected isEditMode(): boolean {
    return this.mode === EDITOR_MODE.EDIT;
  }

  protected isNewMode(): boolean {
    return this.mode === EDITOR_MODE.NEW;
  }

  protected emitWidgetEvent: <T extends AnyObject>(
    widgetName: string,
    eventName: string,
    data: T,
  ) => void = () => {
    const translatedNotConfiguredMessage = this.i18nService.instant(
      I18n.ErrorMessages.NotConfiguredError,
    );
    throw new Error(translatedNotConfiguredMessage);
  };

  protected createInfoMessage(
    params: IInfoMessageParams,
  ): IDocumentInfoMessage {
    return {
      type: DocumentMessageTypes.INFO,
      message: params.message,
    };
  }

  protected createErrorMessage(
    params: IErrorMessageParams,
  ): IDocumentErrorMessage {
    return {
      type: DocumentMessageTypes.ERROR,
      message: params.message,
      errors: params.errors,
    };
  }

  protected getErrors(): IDocumentInfoErrorType[] {
    return castArray(get(this.document, ['meta', 'errors']));
  }

  public schedulingValue(schedulingValue: IScheduleData): IScheduleData {
    if (isEmpty(schedulingValue)) return this.getScheduleValue;
    this.setScheduleValue = schedulingValue;
  }

  public updateItemsByCompanyPreferences(
    data,
    path: string,
    markField: boolean = true,
  ): void {
    // When the user does not configure a list view or configures it as CATEGORY_VIEW
    // the list will be processed directly as a Category view
    if (
      isEqual(this.gridListView, GRID_LIST_VIEW_TYPE.ALPHABETICAL_LIST_VIEW)
    ) {
      const flattenTreeData = flatTreeDataByHasChildren<ITreeData>(data);
      const sortedData = sortBy(flattenTreeData, (item) =>
        toLower(item?.name || item?.item_name),
      );
      setFormFieldValue(this.form, path, sortedData, markField);
    } else {
      setFormFieldValue(this.form, path, data, markField);
    }
  }

  public isCategoryListView(): boolean {
    // When the user does not configure list view or CATEGORY_VIEW, it will return false.
    return isEqual(this.gridListView, GRID_LIST_VIEW_TYPE.CATEGORY_VIEW);
  }

  protected async deletedDocumentHandler(
    infoResult,
  ): Promise<IDocumentInfoMessage> {
    const isDeletedDocument = !get(this.document, 'is_active', true);
    if (!isDeletedDocument) return infoResult;

    this.getCurrentEntity();

    return this.createInfoMessage({ message: this.getInfoMessage() });
  }

  protected getInfoMessage(): string {
    const readableEntityType = startCase(this.currentEntityType);

    return `This ${readableEntityType} is deleted and no longer available.`;
  }

  protected canPrintEmptyPdf(): boolean {
    return false;
  }

  private getCurrentEntity(): void {
    this.subscriptions.push(
      this.currentEntityTypeObs.subscribe(
        (entity) => (this.currentEntityType = entity),
      ),
    );
  }

  private clearSubscriptions(): void {
    this.subscriptions.forEach((sub: Subscription) => sub.unsubscribe());
    this.subscriptions = [];
  }

  private clearOnDestroySubscriptions(): void {
    this.cleanedOnDestroySubscriptions.forEach((sub: Subscription) =>
      sub.unsubscribe(),
    );
    this.cleanedOnDestroySubscriptions = [];
  }

  private clearForm(): void {
    this.form = null;
    this.formPromise = new Promise((res: Function): void => {
      this.formResolver = res;
    });
  }

  private clearDocument(): void {
    this.document = null;
    this.documentPromise = new Promise((res: Function): void => {
      this.documentResolver = res;
    });
  }

  private clearDocumentMessages(): void {
    this.documentMessages = [];
  }

  private getDocumentOpenMode(doc: IBaseEntityType): EDITOR_MODE {
    return get(doc, '_id') ? EDITOR_MODE.EDIT : EDITOR_MODE.NEW;
  }
}
